/**
 * Modal — one component, three widths (spec Decision 2): Medium 720px, Large 1040px, Extra large
 * 90vw; capped at the window less the gutters, full screen below 768. Height grows to 90% and
 * then the body scrolls with the title and the action bar pinned. role="dialog" aria-modal,
 * scrim, focus trapped, Escape and Cancel close, focus returns to the trigger. Actions sit bottom
 * right; the deciding button names the record.
 *
 * ConfirmModal is the confirm variant: key and title, now → after as a two-column table, a
 * required reason in the same view, and the second-administrator line in the footer.
 */
import * as React from 'react';
import { FieldGridBoundary } from './Field';
import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';
import { X } from 'lucide-react';
import { Button, DisabledWhy } from './Button';
import { Field, Textarea } from './controls';

export type ModalSize = 'medium' | 'large' | 'xl';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: ModalSize;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /** Steps of a multi-step flow ("1 Source · 2 Preview · 3 Apply"); `step` is the current index. */
  steps?: readonly string[];
  step?: number;
  children: React.ReactNode;
  /** The action bar (bottom right). */
  footer?: React.ReactNode | ((requestClose: () => void) => React.ReactNode);
  /** A note at the left of the action bar ("3 will be written · 1 needs a fix"). */
  footerNote?: React.ReactNode;
  /** Why the deciding action is disabled: one line under the action bar. */
  footerWhy?: React.ReactNode;
  /** R-15 C: unsaved changes — Escape, ✕ and Cancel ask before the form closes (the single-task forms moved off the drawer) */
  dirty?: boolean;
  'data-testid'?: string;
}

/** Where focus goes when a modal closes: the opener while it is still on the page (not the page body), else the dialog's default. */
export function modalReturnFocus(opener: Pick<HTMLElement, 'isConnected'> | null | undefined): HTMLElement | true {
  return opener && opener.isConnected && (opener as Element).nodeName !== 'BODY' ? (opener as HTMLElement) : true;
}

/** The footer: a node as given, or what a footer function makes of the guarded close. */
export function footerContent(footer: ModalProps['footer'], requestClose: () => void): React.ReactNode {
  return typeof footer === 'function' ? footer(requestClose) : footer;
}

export function Modal({ open, onClose: close, size = 'medium', eyebrow, title, steps, step = 0, children, footer, footerNote, footerWhy, dirty, ...rest }: ModalProps) {
  const [asking, setAsking] = React.useState(false);
  const onClose = () => (dirty ? setAsking(true) : close());
  // R-15 C (rule 22): Escape, ✕ and Cancel return focus to the control that opened the modal. The kit opens its modals
  // from state, not from a Dialog.Trigger, so the opener is the element that had focus when the modal opened.
  const opener = React.useRef<HTMLElement | null>(null);
  const wasOpen = React.useRef(false);
  if (open && !wasOpen.current && typeof document !== 'undefined') opener.current = document.activeElement as HTMLElement | null;
  wasOpen.current = open;
  return (
    <>
    {dirty ? (
      <Modal
        open={asking}
        size="medium"
        eyebrow="Unsaved changes"
        title={<>Discard your changes to {title}?</>}
        onClose={() => setAsking(false)}
        footer={
          <>
            <Button onClick={() => setAsking(false)}>Keep editing</Button>
            <Button
              variant="destructive"
              onClick={() => {
                setAsking(false);
                close();
              }}
            >
              Discard changes
            </Button>
          </>
        }
      >
        <p>Your entries have not been saved. Leaving now discards them.</p>
      </Modal>
    ) : null}
    <BaseDialog.Root open={open} onOpenChange={(o: boolean) => !o && onClose()}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="k-scrim" />
        <BaseDialog.Popup className="k-modal" data-kit="modal" data-size={size} data-testid={rest['data-testid']} aria-modal="true" finalFocus={() => modalReturnFocus(opener.current)}>
          <div className="k-m-head">
            <div className="k-m-titles">
              {eyebrow ? <span className="k-eyebrow">{eyebrow}</span> : null}
              <BaseDialog.Title render={<h2 />}>{title}</BaseDialog.Title>
              {steps?.length ? (
                <div className="k-steps">
                  {steps.map((s, i) => (
                    <span key={s} aria-current={i === step ? 'step' : undefined}>
                      <span className="k-step-n">{i + 1}</span> <span className="k-step-label">{s}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <Button icon aria-label="Close" data-testid="modal-close" onClick={onClose}>
              <X aria-hidden />
            </Button>
          </div>
          <div className="k-m-body">
            <FieldGridBoundary>{children}</FieldGridBoundary>
          </div>
          {footer || footerNote ? (
            <div className="k-m-foot">
              {footerNote ? <span className="k-m-left">{footerNote}</span> : null}
              {footerContent(footer, onClose)}
              {footerWhy ? (
                <div className="k-m-why">
                  <DisabledWhy>{footerWhy}</DisabledWhy>
                </div>
              ) : null}
            </div>
          ) : (
            <div />
          )}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
    </>
  );
}

export interface ChangeRow {
  field: React.ReactNode;
  now: React.ReactNode;
  after: React.ReactNode;
}

/** Field · Now · After, the record's change as a two-column table. */
export function ChangeTable({ rows }: { rows: readonly ChangeRow[] }) {
  return (
    <div className="k-diff" role="table" aria-label="What changes" data-kit="table" data-variant="diff">
      <div role="row" className="k-diff-row">
        <div className="k-diff-h" role="columnheader">Field</div>
        <div className="k-diff-h" role="columnheader">Now</div>
        <div className="k-diff-h" role="columnheader">After</div>
      </div>
      {rows.map((r, i) => (
        <div role="row" className="k-diff-row" key={i}>
          <div role="rowheader">{r.field}</div>
          <div className="k-was" role="cell">{r.now}</div>
          <div className="k-now" role="cell">{r.after}</div>
        </div>
      ))}
    </div>
  );
}

/** The API's limits on a change reason. */
export const REASON_MIN = 3;
export const REASON_MAX = 2000;

/** Why the deciding button is disabled for this reason, or null when the reason is acceptable. */
export function reasonProblem(reason: string, confirmLabel: string): string | null {
  const n = reason.trim().length;
  if (n === 0) return `Give a reason to continue: ${confirmLabel} needs one.`;
  if (n < REASON_MIN) return `The reason needs at least ${REASON_MIN} characters.`;
  if (n > REASON_MAX) return `The reason must be ${REASON_MAX} characters or fewer (it has ${n}).`;
  return null;
}

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  /** What is being done, as the eyebrow ("Retire capability"). */
  action: string;
  record: { key?: string | null; title: string };
  /** Facts about the record shown above the change (Department, Domain…). */
  facts?: ReadonlyArray<{ label: string; value: React.ReactNode }>;
  changes: readonly ChangeRow[];
  /** The deciding button's words, naming the record ("Retire BEE-CAP-0245"). */
  confirmLabel: string;
  reasonLabel?: string;
  /** Shown in the footer when a second administrator applies the change. */
  secondAdmin?: boolean | string;
  destructive?: boolean;
  pending?: boolean;
  onConfirm: (reason: string) => void;
  'data-testid'?: string;
}

export function ConfirmModal({ open, onClose, action, record, facts, changes, confirmLabel, reasonLabel = 'Why (required, stored with the change)', secondAdmin, destructive = true, pending, onConfirm, ...rest }: ConfirmModalProps) {
  const [reason, setReason] = React.useState('');
  const id = React.useId();
  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const problem = reasonProblem(reason, confirmLabel);
  const missing = problem !== null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="medium"
      eyebrow={action}
      title={[record.key, record.title].filter(Boolean).join(' ')}
      data-testid={rest['data-testid']}
      footerNote={secondAdmin ? (typeof secondAdmin === 'string' ? secondAdmin : 'A second administrator applies this change.') : undefined}
      footerWhy={problem ?? undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            disabled={pending || missing}
            onClick={() => {
              if (!missing) onConfirm(reason.trim());
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {facts?.length ? (
        <dl className="k-kv">
          {facts.map((f) => (
            <div key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <ChangeTable rows={changes} />
      <Field label={reasonLabel} htmlFor={id}>
        <Textarea id={id} required aria-required="true" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}
